const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function parseUtcDate(value) {
  if (!value) return null;
  const raw = String(value);
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]);
    const day = Number(dateOnly[3]);
    return new Date(Date.UTC(year, month - 1, day));
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatUtcShort(d) {
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function addUtcMonthsClamped(d, months = 1) {
  const year = d.getUTCFullYear() + Math.floor((d.getUTCMonth() + months) / 12);
  const month = (d.getUTCMonth() + months) % 12;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const day = Math.min(d.getUTCDate(), lastDay);
  return new Date(Date.UTC(year, month, day));
}

function inferCycleEnd(start, cycle, nextCycleStart) {
  const explicitEnd = parseUtcDate(cycle?.end_date || cycle?.range_end);
  if (explicitEnd) return explicitEnd;

  const nextStart = parseUtcDate(nextCycleStart);
  if (nextStart) {
    return nextStart;
  }

  if (start) return addUtcMonthsClamped(start, 1);
  return null;
}

export function formatCycleRangeLabel(cycle, { nextCycleStart } = {}) {
  if (!cycle) return "";
  if (cycle.range_label) return cycle.range_label;
  const start = parseUtcDate(cycle.start_date);
  if (!start) {
    const raw = (cycle.label || "").replace(/\s+cycle\s*$/i, "").trim();
    return raw || "Cycle";
  }
  const end = inferCycleEnd(start, cycle, nextCycleStart);
  if (!end) return formatUtcShort(start);
  return `${formatUtcShort(start)} - ${formatUtcShort(end)}`;
}

export function buildCycleLabelMap(cycles = []) {
  const sorted = [...cycles].sort(
    (a, b) => new Date(a.start_date || 0).getTime() - new Date(b.start_date || 0).getTime(),
  );
  const map = new Map();
  sorted.forEach((cycle, index) => {
    const nextCycleStart = sorted[index + 1]?.start_date;
    map.set(cycle.id, formatCycleRangeLabel(cycle, { nextCycleStart }));
  });
  return map;
}

/** UTC day bounds for a monthly cycle (uses next cycle start when end is missing). */
export function getCycleDateBounds(cycle, { nextCycleStart } = {}) {
  const start = parseUtcDate(cycle?.start_date);
  if (!start) return null;
  const end = inferCycleEnd(start, cycle, nextCycleStart);
  if (!end) return null;
  return { start, end };
}

/**
 * Elapsed progress through [start, end] as of `now` (0–100).
 * Uses calendar time so the bar fills continuously as the timeline advances.
 */
export function dateRangeProgressPercent(start, end, now = new Date()) {
  if (!start || !end) return null;
  const startMs = start.getTime();
  const endMs = end.getTime();
  if (!(endMs > startMs)) return null;
  const nowMs = now instanceof Date && !Number.isNaN(now.getTime()) ? now.getTime() : Date.now();
  const clamped = Math.min(endMs, Math.max(startMs, nowMs));
  return Math.round(((clamped - startMs) / (endMs - startMs)) * 100);
}

/** Progress through the active engagement / cycle window. */
export function engagementTimelineProgressPercent(client, activeCycle, cycles = [], now = new Date()) {
  let start = null;
  let end = null;

  if (client?.engagement_type === "monthly" && activeCycle) {
    const sorted = [...cycles].sort(
      (a, b) => new Date(a.start_date || 0).getTime() - new Date(b.start_date || 0).getTime(),
    );
    const idx = sorted.findIndex((c) => c.id === activeCycle.id);
    const nextCycleStart = idx >= 0 ? sorted[idx + 1]?.start_date : undefined;
    const bounds = getCycleDateBounds(activeCycle, { nextCycleStart });
    start = bounds?.start || null;
    end = bounds?.end || null;
  } else {
    start = parseUtcDate(client?.timeline_start);
    end = parseUtcDate(client?.timeline_end);
  }

  return dateRangeProgressPercent(start, end, now);
}
